import { type RaRecord, useGetIdentity, useTranslate } from "ra-core";

import { RelativeDate } from "../misc/RelativeDate";
import { useGetSalesName } from "../sales/useGetSalesName";
import type { ActivityLoadNoteCreated } from "../types";
import { useActivityLogContext } from "./ActivityLogContext";
import { ActivityLogNote } from "./ActivityLogNote";
import { ReferenceField } from "@/components/admin/reference-field";
import { useIsMobile } from "@/hooks/use-mobile";
import { DOCUMENT_TYPE_CHOICES } from "../loads/LoadInputs";

type ActivityLogLoadNoteCreatedProps = {
  activity: RaRecord & ActivityLoadNoteCreated;
};

export function ActivityLogLoadNoteCreated({
  activity,
}: ActivityLogLoadNoteCreatedProps) {
  const context = useActivityLogContext();
  const isMobile = useIsMobile();
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  const { loadNote } = activity;
  const isCurrentUser = activity.sales_id === identity?.id;
  const salesName = useGetSalesName(activity.sales_id, {
    enabled: !isCurrentUser,
  });
  return (
    <ActivityLogNote
      header={
        <div className="flex flex-row items-start gap-2 flex-grow">
          <div className="w-[20px] h-[20px] bg-gray-300 rounded-full shrink-0" />
          <span className="text-muted-foreground text-sm flex-grow">
            {translate(
              isCurrentUser
                ? "crm.activity.you_added_note_about_load"
                : "crm.activity.added_note_about_load",
              { name: salesName },
            )}{" "}
            <ReferenceField
              source="load_id"
              reference="loads"
              record={loadNote}
              link={isMobile ? false : "show"}
            />
            {context !== "company" && activity.company_id && (
              <>
                {" "}
                {translate("crm.activity.at_company")}{" "}
                <ReferenceField
                  source="company_id"
                  reference="companies"
                  record={activity}
                  link="show"
                />{" "}
                <RelativeDate date={activity.date} />
              </>
            )}
          </span>

          {(context === "company" || !activity.company_id) && (
            <span className="text-muted-foreground text-sm">
              <RelativeDate date={activity.date} />
            </span>
          )}
        </div>
      }
      text={loadNote.text}
      link={isMobile ? false : `/loads/${loadNote.load_id}/show`}
      subject={loadNote.subject}
      documentType={
        loadNote.document_type
          ? DOCUMENT_TYPE_CHOICES.find((c) => c.id === loadNote.document_type)
              ?.name || loadNote.document_type
          : undefined
      }
    />
  );
}
